// Carrito de la toma de pedido. Lógica portada de la Comandera anterior:
// promociones (sugerencia automática y promo armada artículo por artículo),
// modificadores con precio extra y tiempo (1er / 2º / postre) por línea.
import { useCallback, useRef, useState } from 'react';
import { modificadoresService } from '../../services/api';
import type { ModificadorSel } from '../../components/ModalModificadores';

export interface Linea {
  key: string;
  producto_id: string;
  nombre: string;
  precio: number;        // unitario, ya con extras
  cantidad: number;
  tiempo: number;
  notas: string;
  modificadores: ModificadorSel[];
  promo_id?: string; promo_nombre?: string; promo_emoji?: string; promoKey?: string;
}

interface PromoCtx { promo: any; item: any; indice: number; promoKey: string }
export interface Hoja { prod: any; precio: number; grupos: any[]; promo?: PromoCtx }

let seq = 0;
const nuevaKey = () => `l${Date.now()}-${seq++}`;

export function useCarrito(promociones: any[], productos: any[]) {
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [hoja, setHoja] = useState<Hoja | null>(null);
  const [sugerencia, setSugerencia] = useState<any>(null);
  const rechazadas = useRef(new Set<string>());
  const cacheGrupos = useRef<Record<string, any[]>>({});

  const grupos = async (id: string) => {
    if (!cacheGrupos.current[id]) {
      try { cacheGrupos.current[id] = (await modificadoresService.getDeProducto(id)).data; }
      catch { cacheGrupos.current[id] = []; }
    }
    return cacheGrupos.current[id];
  };

  // Si lo que hay en el carrito ya cubre una promo, se sugiere aplicarla.
  const detectar = useCallback((ls: Linea[]) => {
    for (const promo of promociones) {
      if (rechazadas.current.has(promo.id)) continue;
      const cubierta = promo.items.every((pi: any) =>
        ls.filter(l => l.producto_id === pi.producto_id && !l.promo_id).reduce((s, l) => s + l.cantidad, 0) >= pi.cantidad);
      if (cubierta) { setSugerencia(promo); return; }
    }
  }, [promociones]);

  const agregar = (l: Omit<Linea, 'key'>) => setLineas(prev => {
    const nuevo = [...prev, { ...l, key: nuevaKey() }];
    if (!l.promo_id) setTimeout(() => detectar(nuevo), 0);
    return nuevo;
  });

  const tocarProducto = async (prod: any) => {
    setHoja({ prod, precio: parseFloat(prod.precio), grupos: await grupos(prod.id) });
  };

  // Promo: precio de cada artículo prorrateado al precio de la promo.
  const precioEnPromo = (promo: any, item: any) => {
    const normal = promo.items.reduce((s: number, i: any) => s + parseFloat(i.precio_unitario) * i.cantidad, 0);
    return +(parseFloat(item.precio_unitario) * (normal > 0 ? parseFloat(promo.precio) / normal : 1)).toFixed(2);
  };

  const procesarPromo = async (promo: any, indice: number, promoKey: string): Promise<void> => {
    if (indice >= promo.items.length) return;
    const item = promo.items[indice];
    const prod = productos.find(p => p.id === item.producto_id) || { id: item.producto_id, nombre: item.producto_nombre };
    const precio = precioEnPromo(promo, item);
    const gs = await grupos(prod.id);
    if (gs.length) { setHoja({ prod, precio, grupos: gs, promo: { promo, item, indice, promoKey } }); return; }
    agregar({ producto_id: prod.id, nombre: item.producto_nombre || prod.nombre, precio, cantidad: item.cantidad, tiempo: 1, notas: '', modificadores: [],
      promo_id: promo.id, promo_nombre: promo.nombre, promo_emoji: promo.emoji, promoKey });
    return procesarPromo(promo, indice + 1, promoKey);
  };

  const tocarPromo = (promo: any) => procesarPromo(promo, 0, `${promo.id}-${Date.now()}`);

  const confirmarHoja = (sel: { mods: ModificadorSel[]; tiempo: number; notas: string; cantidad: number }) => {
    if (!hoja) return;
    const extra = sel.mods.reduce((s, m) => s + (m.precio_extra || 0), 0);
    const base = { producto_id: hoja.prod.id, nombre: hoja.prod.nombre, precio: hoja.precio + extra, tiempo: sel.tiempo, notas: sel.notas, modificadores: sel.mods };
    const ctx = hoja.promo;
    setHoja(null);
    if (ctx) {
      agregar({ ...base, cantidad: ctx.item.cantidad, promo_id: ctx.promo.id, promo_nombre: ctx.promo.nombre, promo_emoji: ctx.promo.emoji, promoKey: ctx.promoKey });
      setTimeout(() => procesarPromo(ctx.promo, ctx.indice + 1, ctx.promoKey), 0);
    } else {
      agregar({ ...base, cantidad: sel.cantidad });
    }
  };

  // Cancelar a media promo quita lo que ya se había agregado de ella.
  const cancelarHoja = () => {
    const k = hoja?.promo?.promoKey;
    if (k) setLineas(prev => prev.filter(l => l.promoKey !== k));
    setHoja(null);
  };

  const aceptarSugerencia = () => {
    const promo = sugerencia;
    if (!promo) return;
    setSugerencia(null);
    const promoKey = `${promo.id}-${Date.now()}`;
    setLineas(prev => {
      let nuevo = [...prev];
      for (const pi of promo.items) {
        let resta = pi.cantidad;
        nuevo = nuevo.map(l => {
          if (l.producto_id !== pi.producto_id || l.promo_id || resta <= 0) return l;
          const q = Math.min(l.cantidad, resta); resta -= q;
          return { ...l, cantidad: l.cantidad - q };
        }).filter(l => l.cantidad > 0);
      }
      return [...nuevo, ...promo.items.map((pi: any) => ({
        key: nuevaKey(), producto_id: pi.producto_id, nombre: pi.producto_nombre, precio: precioEnPromo(promo, pi),
        cantidad: pi.cantidad, tiempo: 1, notas: '', modificadores: [],
        promo_id: promo.id, promo_nombre: promo.nombre, promo_emoji: promo.emoji, promoKey,
      }))];
    });
  };

  const rechazarSugerencia = () => {
    if (sugerencia) rechazadas.current.add(sugerencia.id);
    setSugerencia(null);
  };

  const cambiar = (key: string, patch: Partial<Linea>) =>
    setLineas(prev => prev.map(l => l.key === key ? { ...l, ...patch } : l));

  return {
    lineas, hoja, sugerencia,
    total: lineas.reduce((s, l) => s + l.precio * l.cantidad, 0),
    nItems: lineas.reduce((s, l) => s + l.cantidad, 0),
    tocarProducto, tocarPromo, confirmarHoja, cancelarHoja, aceptarSugerencia, rechazarSugerencia,
    cantidad: (key: string, d: number) => setLineas(prev => prev.map(l => l.key === key ? { ...l, cantidad: Math.max(1, l.cantidad + d) } : l)),
    tiempo: (key: string, tiempo: number) => cambiar(key, { tiempo }),
    quitar: (key: string) => setLineas(prev => prev.filter(l => l.key !== key)),
    vaciar: () => { setLineas([]); rechazadas.current.clear(); },
    // Lo que espera POST /comandas y PATCH /comandas/:id/items
    items: () => lineas.map(({ key, promoKey, ...l }) => ({ ...l, notas: l.notas || null })),
  };
}
